// pendingSubmitFlush.js
//
// ページ離脱時に未送信データを送り出すための処理。SPEC.md 4.8 段階3
//
// 結果画面が表示された直後にブラウザを閉じる利用者が多いため、
// 「閉じる瞬間」にリクエストを投げておく。
// fetch の keepalive オプションを使うと、ページが破棄されたあとも
// ブラウザがリクエストの完了を引き受けてくれる（Chrome / Safari / Firefox / Edge 対応）。
//
// 【重要】これは「閉じた後に何度も再試行する」仕組みではない。
// Web アプリはタブを閉じた後に JavaScript を動かし続けられないため、
// 離脱時に1回投げるのが限界。閉じた後の再試行には Service Worker +
// Background Sync API が必要だが、Safari / iOS が非対応のため採用していない。

const PENDING_KEY_PREFIXES = [
    'temp_form_data_',
    'pending_results_data_',
    'temp_thankyou_data_'
];

// form_submit テーブルに存在するカラムのみを送る。
// localStorage には timestamp や temp_data など、カラムではない項目も入っているため、
// そのまま POST すると Supabase 側でエラーになる。
// frax_major / frax_hip は意図的に含めない（既定値の NULL が入る）。SPEC.md 4.7(b)
const FORM_SUBMIT_COLUMNS = [
    'session_id',
    'complete',
    'risk',
    'osta_value',
    'gender',
    'age',
    'height',
    'weight',
    'dental_check',
    'dental_issue',
    'previous_fracture',
    'parent_hip_fracture',
    'current_smoking',
    'alcohol',
    'glucocorticoids',
    'rheumatoid_arthritis',
    'secondary_osteoporosis',
    'osteoporosis_treatment',
    'bone_density_test'
];

const VALID_RISKS = ['blue', 'yellow', 'red'];

// 同じデータを二重に送らないための、送信中キーの記録
const inFlightKeys = new Set();

const getPendingKeys = () => {
    const keys = [];
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && PENDING_KEY_PREFIXES.some(prefix => key.startsWith(prefix))) {
                keys.push(key);
            }
        }
    } catch (error) {
        console.error('離脱時送信: localStorage の走査に失敗しました:', error);
    }
    return keys;
};

/**
 * localStorage の1件を form_submit の行に変換する。
 * 送信すべきでない場合は null を返す。
 */
const toFormSubmitRow = (pendingData) => {
    if (!pendingData || typeof pendingData !== 'object') {
        return null;
    }

    // session_id が無いものは送らない。
    // 省略すると Supabase が gen_random_uuid() でランダムな値を生成してしまい、
    // sessions テーブルのどの行とも紐づかない孤立レコードになる。SPEC.md 4.8
    if (!pendingData.session_id) {
        return null;
    }

    const row = {};
    FORM_SUBMIT_COLUMNS.forEach(column => {
        if (pendingData[column] !== undefined) {
            row[column] = pendingData[column];
        }
    });

    // 判定完了として記録するなら、risk が正規の値であることを必須とする。
    // 捏造した判定結果を記録しないため。SPEC.md 4.8 原則5
    if (row.complete === true && !VALID_RISKS.includes(row.risk)) {
        return null;
    }

    return row;
};

/**
 * 未送信データを Supabase REST へ送り出す。
 * ページ離脱時に呼ばれるため、await せず即座に返る。
 */
export const flushPendingSubmits = () => {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
        console.error('離脱時送信: Supabase の接続情報がありません');
        return;
    }

    const keys = getPendingKeys();
    if (keys.length === 0) {
        return;
    }

    console.log(`離脱時送信: ${keys.length}件の未送信データを送出します`);

    keys.forEach(key => {
        if (inFlightKeys.has(key)) {
            return;
        }

        let row;
        try {
            row = toFormSubmitRow(JSON.parse(localStorage.getItem(key)));
        } catch (error) {
            console.error('離脱時送信: データの解析に失敗しました:', key, error);
            return;
        }

        if (!row) {
            // 送信条件を満たさないデータは削除せずに残す。
            // 次回起動時の一括再送信で改めて扱う。
            console.warn('離脱時送信: 送信条件を満たさないため見送ります:', key);
            return;
        }

        inFlightKeys.add(key);

        try {
            fetch(`${supabaseUrl}/rest/v1/form_submit`, {
                method: 'POST',
                keepalive: true, // ページが閉じられても送信を完了させる
                headers: {
                    'Content-Type': 'application/json',
                    apikey: supabaseAnonKey,
                    Authorization: `Bearer ${supabaseAnonKey}`,
                    Prefer: 'return=minimal'
                },
                body: JSON.stringify([row])
            })
                .then(response => {
                    if (response.ok) {
                        // ページがまだ生きていれば削除できる。
                        // 閉じられて削除できなかった場合は次回起動時の処理で
                        // 重複チェックされるため、二重登録にはならない。
                        try {
                            localStorage.removeItem(key);
                        } catch (error) {
                            console.error('離脱時送信: 一時データの削除に失敗:', key, error);
                        }
                        console.log('離脱時送信: 送信に成功しました:', key);
                    } else {
                        console.error('離脱時送信: 送信に失敗しました:', key, response.status);
                    }
                })
                .catch(error => {
                    console.error('離脱時送信: 送信中にエラー:', key, error);
                })
                .finally(() => {
                    inFlightKeys.delete(key);
                });
        } catch (error) {
            inFlightKeys.delete(key);
            console.error('離脱時送信: リクエストを開始できませんでした:', key, error);
        }
    });
};

/**
 * ページ離脱時のイベントを購読する。解除用の関数を返す。
 *
 * pagehide だけでなく visibilitychange も購読する理由:
 * - iOS Safari では pagehide が発火しないことがある
 * - タブ切り替えやホーム画面に戻した時点でも送信を試みられる
 */
export const registerPendingSubmitFlush = () => {
    const onPageHide = () => flushPendingSubmits();
    const onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
            flushPendingSubmits();
        }
    };

    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
        window.removeEventListener('pagehide', onPageHide);
        document.removeEventListener('visibilitychange', onVisibilityChange);
    };
};
